import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { images } from '../utils/images';
import { 
  CheckCircle2, 
  Users, 
  Heart,
  Phone,
  Shield,
  Award,
  Home,
  Activity,
  Smile,
  TrendingUp
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import StickyCallButton from '../components/StickyCallButton';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import VideoBackground from '../components/VideoBackground';

const DisabilitySupportPage = () => {
  const services = [
    {
      icon: Users,
      title: "Personal care",
      description: "Support with daily activities such as bathing, dressing and grooming and support with meal preparation and feeding."
    },
    {
      icon: Home,
      title: "In-home support",
      description: "Help with general housekeeping, medication management and companionship, or running general day to day tasks."
    },
    {
      icon: Activity,
      title: "Therapy services",
      description: "Transportation to physical, occupational or speech therapy services to improve mobility, daily functioning, and communication skills, or support and assistance during rehabilitation sessions."
    },
    {
      icon: Smile,
      title: "Support to join social activities",
      description: "Engaging in activities such as sports, arts and social outings, or getting involved in programs that encourage social interaction and community participation."
    },
    {
      icon: Award,
      title: "Job Help",
      description: "Support in the workplace to ensure comfort and well-being."
    },
    {
      icon: Shield,
      title: "Nursing support",
      description: "Medical care provided by licensed nurses such as wound care, medication administration, and monitoring of health conditions."
    },
    {
      icon: Heart,
      title: "Relief care services",
      description: "Temporary relief for primary caregivers, allowing them to rest and recharge."
    }
  ];

  const benefits = [
    {
      icon: TrendingUp,
      title: "Increased independence",
      description: "Individuals can perform daily tasks and help participants live more independently."
    },
    {
      icon: Heart,
      title: "Improving quality of life",
      description: "Access to necessary support leads to a more fulfilling and enjoyable life."
    },
    {
      icon: Activity,
      title: "Improved health outcomes",
      description: "Regular medical care and therapy contribute to better overall health."
    },
    {
      icon: Users,
      title: "Connecting with others",
      description: "Participation in social and recreational activities fosters a sense of belonging."
    },
    {
      icon: Shield,
      title: "Support for caregivers",
      description: "Respite care and other services provide relief and support to primary caregivers."
    },
    {
      icon: Award,
      title: "Customised Care Plans",
      description: "Support services were designed to meet all individual personal needs. So, NDIS providers offer customised plans that help people achieve their goals, preferences, and abilities for personalised care."
    }
  ];

  const faqs = [
    {
      question: "How can I access NDIS support services in Logan or Brisbane?",
      answer: "Life Care and Wellness is a NDIS consulting firm supporting participants in Logan and Brisbane. To find NDIS support services in Logan or Brisbane, you can reach out to our well experienced team or connect with a local area coordinator."
    },
    {
      question: "What services are included in NDIS disability support?",
      answer: "The NDIS offers a variety of funded support categories customized to help participants achieve greater independence and improve their quality of life. These supports are personalized to individual needs and may include: Assistance with Daily Life, Transport, Consumables, Assistance with Social & Community Participation, Assistive Technology, Home Modifications, Coordination of Supports, Improved Living Arrangements."
    },
    {
      question: "How does NDIS help with community access and social participation?",
      answer: "The National Disability Insurance Scheme (NDIS) helps participants with community access and social participation by funding supports that allow them to engage in activities they enjoy and connect with others. This includes funding for social and fun activities, assistance with daily tasks, and capacity building to boost their ability to participate in the community."
    },
    {
      question: "Do you offer in-home disability support in Logan or Brisbane?",
      answer: "Yes, we do. Life Care and Wellness is an NDIS provider offering in-home disability support services in both Logan and Brisbane. Our well trained and caring and friendly support workers can help with: Daily living tasks, overnight support, community outings, social participation & care for families."
    },
    {
      question: "How do you tailor services to meet individual needs?",
      answer: "We customize our services by taking the time to understand each participant's preferences and support needs. We do personalized planning, Goal focused support, flexible delivery."
    },
    {
      question: "What happens if I need to change my NDIS plan?",
      answer: "If you need to change the current NDIS plan you can contact our helpful NDIS team. They will review your request and adjust your plan accordingly. At Life Care and Wellness, we're happy to guide you through the process and help you get the support you need."
    },
    {
      question: "Are your disability support workers qualified and trained?",
      answer: "Support workers play an important key role in making sure people receive the assistance they require over their lifetime. Our team is fully qualified, trained and well experienced and they make sure they deliver high quality care."
    },
    {
      question: "Can I customise my NDIS support services based on my needs?",
      answer: "Yes, absolutely. We believe in providing services based on the person's unique goals and preferences. Our team will closely support you and ensure you align your services with your NDIS goals and give you the choice."
    },
    {
      question: "How do I switch my current NDIS provider to your service?",
      answer: "In this scenario, you need to check the service plan agreement and notify the NDIS provider, who will contact you and create a new service agreement accordingly and start your new services."
    },
    {
      question: "How often can my NDIS plan be reviewed or adjusted?",
      answer: "At Life Care and Wellness, we are also happy to help you prepare for your plan review or adjustment to ensure your support matches your needs. Your NDIS plan is normally checked every 12-36 months, depending on your needs and goals. To request a change you can directly contact us or speak to our NDIS support coordinator. They can guide you throughout the process."
    }
  ];

  const scrollToContact = () => {
    window.location.href = '/contact';
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO title="NDIS Disability Support Services" description="Comprehensive NDIS disability support services in Brisbane and Logan. Daily support, community access, and personalised care to help you live independently." />
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
            <div>
              <h1 
                className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6"
                style={{ color: '#587740' }}
              >
                NDIS Disability Support Services
              </h1>
              <p 
                className="text-lg md:text-xl leading-relaxed mb-4"
                style={{ color: '#5e5e5e' }}
              >
                You deserve support that treats you like a person — not a task. Too many NDIS providers deliver the bare minimum and move on. We don't. Our disability support team in Logan and Brisbane is here to help you live the life you choose, with dignity, respect, and genuine care.
              </p>
              <p 
                className="text-lg leading-relaxed mb-4"
                style={{ color: '#5e5e5e' }}
              >
                In plain language — we help with daily living, getting out into the community, personal care, household tasks, and anything else you need to feel independent and confident. We match you with support workers who actually care.
              </p>
              <p 
                className="text-base leading-relaxed italic mb-4"
                style={{ color: '#6c935e' }}
              >
                We see the psychological struggles that go unseen. You are heard and valued and in control.
              </p>
              <p 
                className="text-sm font-semibold mb-6"
                style={{ color: '#587740' }}
              >
                Free consultation and personalised action plan — let us understand your needs first, at no cost.
              </p>
              <Button
                onClick={scrollToContact}
                size="lg"
                className="text-white hover:opacity-90 transition-opacity"
                style={{ backgroundColor: '#81b956' }}
              >
                <Phone className="w-5 h-5 mr-2" />
                Contact Us
              </Button>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src={images.supportCoordinator3}
                alt="NDIS Disability Support - Art therapy"
                className="w-full h-[400px] md:h-[500px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* What Are NDIS Services Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 
                className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6"
                style={{ color: '#587740' }}
              >
                What Are NDIS Disability Support Services?
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-12 items-center mb-12">
              <div>
                <p 
                  className="text-lg leading-relaxed mb-4"
                  style={{ color: '#5e5e5e' }}
                >
                  In Australia, nearly millions of people suffer from some kind of disability. Hence, 
                  disability support services are designed to help individuals and improve various aspects 
                  of their daily lives. NDIS Disability Support Services in Logan provide each individual a 
                  broad range of assistance for physical, intellectual, developmental, sensory, mental or 
                  psycho-social, and learning disabilities.
                </p>
                <p 
                  className="text-lg leading-relaxed mb-4"
                  style={{ color: '#5e5e5e' }}
                >
                  NDIS service providers explore the different types of disability support services available, 
                  their benefits, and how to access them so you can make a more informed decision about the 
                  care and support you or your loved one needs.
                </p>
                <p 
                  className="text-lg leading-relaxed"
                  style={{ color: '#5e5e5e' }}
                >
                  To put it simply, disability support services include a vast range of assistance designed 
                  to help those with disabilities manage their daily activities, achieve their goals, and 
                  participate in their communities. These services are usually designed to meet the unique 
                  needs of each individual and may include physical, social and emotional support.
                </p>
              </div>
              <div className="rounded-xl overflow-hidden shadow-lg">
                <img 
                  src={images.supportCoordinator1}
                  alt="NDIS support worker with participant"
                  className="w-full h-[400px] object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Services Section */}
      <section className="py-20" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 
                className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6"
                style={{ color: '#587740' }}
              >
                Our Disability Support Services in Logan & Brisbane
              </h2>
              <p 
                className="text-lg md:text-xl max-w-4xl mx-auto"
                style={{ color: '#5e5e5e' }}
              >
                Are you looking for a reliable and wide range of disability care in Logan? Ensure to 
                contact the well experienced, trusted NDIS disability support service provider at Life 
                Care and Wellness. We are responsible to help disabled people with our disability support 
                services and take care of funding.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {services.map((service, index) => {
                const Icon = service.icon;
                return (
                  <Card 
                    key={index}
                    className="border-none shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    <CardContent className="p-8">
                      <div 
                        className="w-16 h-16 rounded-full flex items-center justify-center mb-6"
                        style={{ backgroundColor: '#AABC5A' }}
                      >
                        <Icon className="w-8 h-8 text-white" />
                      </div>
                      <h3 
                        className="text-xl font-bold mb-4"
                        style={{ color: '#587740' }}
                      >
                        {service.title}
                      </h3>
                      <p 
                        className="text-base leading-relaxed"
                        style={{ color: '#5e5e5e' }}
                      >
                        {service.description}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            <div 
              className="p-8 rounded-2xl text-center"
              style={{ backgroundColor: 'white' }}
            >
              <p 
                className="text-lg leading-relaxed mb-6"
                style={{ color: '#5e5e5e' }}
              >
                If you would like to discover how Life Care and Wellness can optimally meet the specific 
                requirements of yourself or someone you care about, don't hesitate to contact us today. 
                You can email us or call us.
              </p>
              <Button
                onClick={scrollToContact}
                size="lg"
                className="text-white hover:opacity-90 transition-opacity"
                style={{ backgroundColor: '#81b956' }}
              >
                <Phone className="w-5 h-5 mr-2" />
                Contact Us
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 
                className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6"
                style={{ color: '#587740' }}
              >
                Why Choose Our NDIS Services?
              </h2>
              <p 
                className="text-lg md:text-xl"
                style={{ color: '#5e5e5e' }}
              >
                Disability support services help people with disabilities get the same chances and 
                support they need to live well and reach their goals.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-12 items-center mb-12">
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img 
                  src={images.supportCoordinator2}
                  alt="Support worker helping with daily living tasks"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="space-y-6">
                {benefits.map((benefit, index) => {
                  const Icon = benefit.icon;
                  return (
                    <div 
                      key={index}
                      className="flex items-start space-x-4 p-4 rounded-lg hover:shadow-md transition-shadow"
                      style={{ backgroundColor: '#f8f9fa' }}
                    >
                      <div 
                        className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: '#81b956' }}
                      >
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 
                          className="text-lg font-bold mb-2"
                          style={{ color: '#587740' }}
                        >
                          {benefit.title}
                        </h3>
                        <p 
                          className="text-base leading-relaxed"
                          style={{ color: '#5e5e5e' }}
                        >
                          {benefit.description}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div 
              className="p-8 rounded-2xl text-center"
              style={{ backgroundColor: '#f8f9fa' }}
            >
              <p 
                className="text-xl leading-relaxed"
                style={{ color: '#5e5e5e' }}
              >
                Disability support services are a lifeline for millions of individuals with disabilities 
                and their families. They can have a huge positive impact on one's life improving their 
                quality of life significantly.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How to Get Started Section */}
      <section className="py-20" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 
                className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6"
                style={{ color: '#587740' }}
              >
                How to Get Started with Our NDIS Support Services
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div 
                className="p-8 rounded-xl bg-white shadow-md"
              >
                <h3 
                  className="text-2xl font-bold mb-4"
                  style={{ color: '#587740' }}
                >
                  Trusted Partner in Brisbane & Logan
                </h3>
                <p 
                  className="text-lg leading-relaxed mb-4"
                  style={{ color: '#5e5e5e' }}
                >
                  As a leading NDIS disability service provider in Brisbane, Life Care and Wellness 
                  delivers compassionate, person-centred support you can trust. We are dedicated to 
                  delivering high-quality and reliable disability support services personalized to 
                  individual needs.
                </p>
                <p 
                  className="text-lg leading-relaxed"
                  style={{ color: '#5e5e5e' }}
                >
                  With a good experience, we specialise in helping people become more independent, 
                  promoting well-being, and enabling community participation through personalised care 
                  and assistance.
                </p>
              </div>

              <div 
                className="p-8 rounded-xl bg-white shadow-md"
              >
                <h3 
                  className="text-2xl font-bold mb-4"
                  style={{ color: '#587740' }}
                >
                  Comprehensive Support Services
                </h3>
                <p 
                  className="text-lg leading-relaxed mb-4"
                  style={{ color: '#5e5e5e' }}
                >
                  Contact us today if you are seeking excellent quality NDIS support services that 
                  provide mental health support, life skills training, and promote social inclusion. 
                  We are your trusted partner for reliable disability support services in Australia.
                </p>
                <p 
                  className="text-lg leading-relaxed font-semibold"
                  style={{ color: '#587740' }}
                >
                  If you are looking for a disability support service and you're not sure how to get 
                  started, please feel free to get in touch with us by calling Kris on 1300 404 229 
                  and we will help you find the support you need.
                </p>
              </div>
            </div>

            <div className="text-center">
              <Button
                onClick={scrollToContact}
                size="lg"
                className="text-white hover:opacity-90 transition-opacity"
                style={{ backgroundColor: '#81b956' }}
              >
                <Phone className="w-5 h-5 mr-2" />
                Contact Us Today
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 
                className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6"
                style={{ color: '#587740' }}
              >
                Frequently Asked Questions
              </h2>
            </div>

            <Accordion type="single" collapsible className="w-full space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="border rounded-lg px-6 shadow-sm"
                >
                  <AccordionTrigger 
                    className="text-left hover:no-underline py-6"
                    style={{ color: '#587740' }}
                  >
                    <span className="text-lg font-semibold pr-4">{faq.question}</span>
                  </AccordionTrigger>
                  <AccordionContent 
                    className="text-base leading-relaxed pb-6"
                    style={{ color: '#5e5e5e' }}
                  >
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            <div className="text-center mt-12">
              <Button
                onClick={scrollToContact}
                size="lg"
                className="text-white hover:opacity-90 transition-opacity"
                style={{ backgroundColor: '#81b956' }}
              >
                <Phone className="w-5 h-5 mr-2" />
                Contact Us
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <VideoBackground variant="dark" className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
              You Deserve Support That Actually Supports You
            </h2>
            <p className="text-lg md:text-xl mb-8 text-white/90">
              Don't settle for providers who treat you like a number. Free consultation, free action plan — let's talk about what you really need and how we can make it happen.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={scrollToContact}
                size="lg"
                className="text-base px-8 py-6"
                style={{ backgroundColor: '#FFFFFF', color: '#587740' }}
              >
                <Phone className="w-5 h-5 mr-2" />
                Get Your Free Consultation
              </Button>
              <a href="/get-on-ndis-free">
                <Button
                  size="lg"
                  variant="outline"
                  className="text-base px-8 py-6 border-2 border-white text-white hover:bg-white/10"
                >
                  Get on the NDIS Free
                </Button>
              </a>
            </div>
          </div>
        </div>
      </VideoBackground>

      <Footer />
      <StickyCallButton />
    </div>
  );
};

export default DisabilitySupportPage;
